// packages/front/src/_common/hooks/use-api-query.ts
//
// Thin wrapper over useQuery that uses apiFetch as the queryFn.
// All server-state reads in page-components go through this hook (or portfolio.hooks etc.
// which build on it). No direct apiFetch in *-page.tsx.

import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { apiFetch } from "../lib/http-client";

export type UseApiQueryOptions<T> = Omit<
  UseQueryOptions<T, Error, T, readonly unknown[]>,
  "queryKey" | "queryFn"
> & {
  timeoutMs?: number;
  headers?: HeadersInit | (() => HeadersInit);
};

export function useApiQuery<T>(
  queryKey: readonly unknown[],
  url: string,
  options?: UseApiQueryOptions<T>,
) {
  const { timeoutMs, headers, ...queryOptions } = options ?? {};
  return useQuery<T, Error, T, readonly unknown[]>({
    queryKey,
    queryFn: ({ signal }) => {
      const requestHeaders = typeof headers === "function" ? headers() : headers;
      return apiFetch<T>(url, {
        signal,
        ...(timeoutMs === undefined ? {} : { timeoutMs }),
        ...(requestHeaders === undefined ? {} : { headers: requestHeaders }),
      });
    },
    ...queryOptions,
  });
}
